import HBIcon from "@/components/HBIcon";
import { BuyVipDialog } from "@/components/Vip";
import { AB_DETAIL_MATERIAL_INFO } from "@/constants/ABMng";
import { USER_RIGHT_STATUS } from "@/constants/biz";
import { EVENT } from "@/constants/event";
import {
  AUTH_RIGHT_PACKAGE_CODES,
  getVipTypeByUser,
  isBigCompanyByUser,
  isCompanyVipByUser,
} from "@/constants/vip";
import useAppStore from "@/context/store";
import { getBizUserRightList, getDeliveryDetail, getGrocery } from "@/services";
import { saveDownloadClickCount } from "@/utils/download";
import DownloadProcess from "@/utils/downloadProcess";
import AppEvent from "@/utils/event";
import { isGdMerged } from "@/utils/gaoding";
import { isPlusByGdVip } from "@/utils/gaodingVip";
import getTimeDuration from "@/utils/getTimeDuration";
import useABTest from "@/utils/hooks/useABtest";
import { usePageEnterRecord } from "@/utils/hooks/usePageEnterRecord";
import { isThirdPartyMaterial } from "@/utils/materialUtils";
import { PRESETS_CONDITION, stewardInstance } from "@/utils/steward";
import useRequest from "@/utils/useReq";
import {
  useBoolean,
  useMemoizedFn,
  useMount,
  useSessionStorageState,
  useSetState,
} from "ahooks";
import { Alert, Button } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { at } from "lodash";
import { useRouter } from "next/router";
import qs from "qs";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { When } from "react-if";
import UserGuideTooltip from "../UserGuideTooltip";
import styles from "./index.module.less";

// 花瓣素材
function PinMaterial({ pin, searchWord }) {
  const router = useRouter();
  const downloadDialogRef = useRef();
  const infoAbtest = useABTest(AB_DETAIL_MATERIAL_INFO);

  // 在线编辑展示规则：https://doc.huanleguang.com/pages/viewpage.action?pageId=381429564
  const [showEditOnline, setShowEditOnline] = useState(false);

  usePageEnterRecord(PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES, [
    pin,
  ]);

  useMemo(async () => {
    if (!pin) {
      setShowEditOnline(false);
      return;
    }
    const downloadProcess = new DownloadProcess({
      pin,
    });
    const hide = await downloadProcess.checkHideEditOnline();
    setShowEditOnline(!hide);
  }, [pin]);

  const { vips, user, config } = useAppStore(({ vips, user, config }) => ({
    vips,
    user,
    config,
  }));

  const openMaterialAuthTableDialog = useMemoizedFn(async () => {
    const { MaterialAuthTableDialog, MaterialAuthType } = await import(
      "../MaterialAuthTable"
    );
    MaterialAuthTableDialog.open({
      category: pin?.file_material?.material_category,
      activeKey: [
        MaterialAuthType.online,
        MaterialAuthType.offline,
        MaterialAuthType.custom,
      ],
    });
  });

  const { data: materialAuth } = useRequest(
    async () => {
      let vipType = getVipTypeByUser(user);

      if (isGdMerged(user)) {
        const version = vips[0]?.solution_version;

        /** 授权范围 */
        const authType = {
          BASIC: "company",
          PROFESSIONAL: "extendCompany",
          FLAGSHIP: "bigCompany",
          PERSONAL_LEARNING: "personalNoAuth",
          CORPORATE_STANDARD: "company",
          CORPORATE_PRO: "extendCompany",
          PERSONAL_COMMERCIAL: "personal",
          PERSONAL: "personal",
          HUA_BAN_TEMPLATE_LEARNING: "singleNoAuth",
          HUA_BAN_TEMPLATE_COMMERCIAL: "single",
        };

        vipType = authType[version] || "no";

        if (isPlusByGdVip(vips) && version === "BASIC") {
          vipType = "extendCompany";
        }
      }

      const more = (
        <a
          data-gd-click="button_click"
          data-button-name="授权详情"
          onClick={openMaterialAuthTableDialog}
        >
          详情
        </a>
      );

      if (isCompanyVipByUser(user) && !isGdMerged(user)) {
        const rights = await getBizUserRightList({
          status: USER_RIGHT_STATUS.NORMAL,
          not_expired: 1,
        });

        if (
          rights.list?.length &&
          rights.list.some((item) =>
            AUTH_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
          )
        ) {
          return <>您已获得企业高级授权，{more}</>;
        }
      }

      const authMap = {
        singleNoAuth: <>您已获得个人学习授权、不可商用，{more}</>,
        single: <>您已获得个人商用授权，{more}</>,
        personalNoAuth: <>您已获得个人学习授权、不可商用，{more}</>,
        personal: <>您已获得个人商用授权，{more}</>,
        company: <>您已获得企业标准授权，{more}</>,
        extendCompany: <>您已获得企业高级授权，{more}</>,
        bigCompany: (
          <>您已获得企业定制授权，{more}（“按需定制”即以线下合同为准）</>
        ),
        no: (
          <>
            <a
              data-gd-click="button_click"
              data-button-name="线上商用"
              onClick={async () => {
                const { MaterialAuthTableDialog, MaterialAuthType } =
                  await import("../MaterialAuthTable");
                MaterialAuthTableDialog.open({
                  category: pin?.file_material?.material_category,
                  activeKey: MaterialAuthType.online,
                });
              }}
            >
              线上商用
            </a>
            I
            <a
              data-gd-click="button_click"
              data-button-name="线下印刷"
              onClick={async () => {
                const { MaterialAuthTableDialog, MaterialAuthType } =
                  await import("../MaterialAuthTable");
                MaterialAuthTableDialog.open({
                  category: pin?.file_material?.material_category,
                  activeKey: MaterialAuthType.offline,
                });
              }}
            >
              线下印刷
            </a>
            I
            <a
              data-gd-click="button_click"
              data-button-name="户外/敏感/转售授权"
              onClick={async () => {
                const { default: FromDialog, LeadsEntrance } = await import(
                  "@/pageComponents/Consult/FormModal"
                );
                const config = await getGrocery("company-consult-v2");
                FromDialog.open({
                  config,
                  leads_entrance: LeadsEntrance.PinDetail,
                });
              }}
            >
              户外{!pin?.file_material?.sensitivity && `/敏感`}/转售授权
            </a>
            <div>具体以您开通会员的授权范围为准</div>
          </>
        ),
      };

      return authMap[vipType];
    },
    { refreshDeps: [user] }
  );

  const info = pin.material_info;
  const material = pin.file_material;
  const [downloadBubble, setDownloadBubble] = useSetState({
    visible: false,
    props: {},
  });

  const [expireHasShow, setExpireHasShow] =
    useSessionStorageState("expireAlertHasShow");

  // 到期提醒
  const expireTip = useMemo(() => {
    if (!vips?.length || isBigCompanyByUser(user)) {
      return false;
    }

    let vip_expire = user?.extra?.material_vip_expire_at;

    if (isGdMerged(user)) {
      vip_expire = Math.max(
        ...vips.map(({ expires_at }) => new Date(expires_at))
      );
    }

    const days = dayjs(vip_expire).diff(dayjs(), "d", true);
    if (days <= (config?.expireTipDays || -1)) {
      const content = (
        <div>
          素材VIP将在
          {getTimeDuration({
            time: vip_expire,
            afterText: "后",
            reverse: true,
          })}
          到期{" "}
          <a
            className="primary-text-btn"
            onClick={() => {
              BuyVipDialog.open({
                isGdDialog: true,
                source_location: "续费_素材信息",
              });
              setExpireHasShow(true);
            }}
          >
            立即续费
          </a>
        </div>
      );
      return {
        days,
        content,
      };
    }
    return false;
  }, [config?.expireTipDays, setExpireHasShow, user, vips]);

  const { data: downloadTip = "" } = useRequest(async () => {
    const result = await getDeliveryDetail("detail_download_label");
    return result?.pit_modules?.[0]?.material?.title || "";
  });

  const files = useMemo(() => {
    const info = pin.material_info;
    const material = pin.file_material;

    let files = [];

    if (
      material.material_category === "平面模板" ||
      material.material_category === "视频模板"
    ) {
      const file = info?.files?.find((file) => file.file_type === "ORIGIN");
      if (file) {
        files.push(file);
      }
    } else if (material.material_category === "视频") {
      const mps = info?.content?.mps?.find((mps) => {
        return mps.name === "HD";
      });
      if (mps) {
        files.push({
          file_format: mps.file_format,
          file_size: mps.file_size,
          file_type: mps.name,
        });
      }
    } else {
      files = info.files;
    }

    return files;
  }, [pin]);

  const size = useMemo(() => {
    const material = pin.file_material;
    const info = pin.material_info;
    const content = info?.content;

    if (material.material_category === "平面模板") {
      if (content && content.layouts) {
        return {
          width: content.layouts[0].width,
          height: content.layouts[0].height,
        };
      } else {
        return {
          width: info.preview.width,
          height: info.preview.height,
        };
      }
    } else if (
      material.material_category === "图片" &&
      // 兼容一批背景图逻辑
      info.type !== "background"
    ) {
      // 兼容AIGC图片
      if (info?.authorization?.name?.endsWith("AIGC")) {
        return {
          width: info.preview.width,
          height: info.preview.height,
        };
      } else {
        if (content.originWidth && content.originHeight) {
          return {
            width: content.originWidth,
            height: content.originHeight,
          };
        }
      }
    }

    return null;
  }, [pin]);

  const copyright = useMemo(() => {
    if (
      material.material_category === "图片" ||
      material.material_category === "视频"
    ) {
      const copyright = String(
        at(pin, "material_info.content.extra.copyright")[0] || ""
      );

      // 包含"已取得""已获得"的进行展示，其余的则不展示"版权说明"
      if (
        copyright &&
        (copyright.includes("已取得") || copyright.includes("已获得"))
      ) {
        return copyright;
      }

      return "";
    }

    // 其他默认已获得
    return "已获得著作权/肖像权授权";
  }, [material.material_category, pin]);

  // 署名
  // const name = useMemo(() => {
  //   return at(pin, "material_info.content.extra.credit_line")[0];
  // }, [pin]);

  const buttons = useMemo(() => {
    return files.map((file, index) => {
      const size = file.file_size;
      const format = file.file_format?.toUpperCase();
      return (
        <Button
          className={styles.button}
          type="primary"
          icon={infoAbtest.old && <HBIcon type="x" />}
          onClick={() => {
            const downloadProcess = new DownloadProcess({
              pin,
              file,
              searchWord,
            });
            const { count } = saveDownloadClickCount();
            downloadProcess.processDownloadMaterial(count);
          }}
          data-gd-click="button_click"
          data-button-name="下载"
          data-content-source={`${index + 1}_下载${format}`}
          data-search-word={searchWord}
          data-board-id={pin?.board_id}
          data-content-id={pin.id}
          key={file.file_type}
          data-iscopyright={1}
          id="material_download_btn"
        >
          <div className={styles.container}>
            <div className={styles.text}>下载{format}</div>
            <When condition={!!Number(size)}>
              <div className={styles.subtext}>
                {(size / (1024 * 1024)).toFixed(2)}M
              </div>
            </When>
          </div>
          <When condition={downloadTip && files.length === 1}>
            <div className={styles.label}>{downloadTip}</div>
          </When>
        </Button>
      );
    });
  }, [files, infoAbtest.old, searchWord, pin, downloadTip]);

  useEffect(() => {
    if (router.query.pinId != pin?.pin_id) {
      // 切换页面时，pin获取是异步的，所以需要等pin获取到了再执行
      return;
    }

    const clickDownload =
      router.query.clickDownload ||
      qs.parse(location.search.slice(1))?.clickDownload;

    // fix: 解决跳转到没有下载的素材时，下载弹窗不消失的问题
    if (downloadDialogRef.current?.destroy) {
      downloadDialogRef.current.destroy();
    }

    if (clickDownload && files[clickDownload]) {
      const downloadProcess = new DownloadProcess({
        pin,
        file: files[clickDownload],
        searchWord,
      });
      downloadProcess.processDownloadMaterial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.clickDownload, pin?.pin_id]);

  useEffect(() => {
    const onPinTimesN = async (getData) => {
      const data = await getData();
      if (data) {
        setDownloadBubble({
          visible: true,
          props: data,
        });
      }
    };
    AppEvent.on(EVENT.PIN_MATERIAL_TIME_N, onPinTimesN);

    return () => {
      AppEvent.off(EVENT.PIN_MATERIAL_TIME_N, onPinTimesN);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMount(() => {
    stewardInstance.getPositionDelivery({
      "/pins/:pinId": ["material_detail_dialog", "material_detail_nps"],
    });
  });

  const [infoExpand, { toggle: toogleExpand }] = useBoolean(false);

  if (files.length <= 0) {
    return null;
  }

  return (
    <div className={styles.material}>
      {expireTip && !expireHasShow && (
        <Alert
          className={styles.expireAlert}
          message={expireTip.content}
          onClose={() => {
            setExpireHasShow(true);
          }}
          type="error"
          icon={<HBIcon type="ic_info" />}
          showIcon
          closable
          closeIcon={<HBIcon type="ic_clear2" />}
        />
      )}
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.material_info?.id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.name}>花瓣素材</div>
          <div className={styles.link}>
            <span className={styles.text}>{config?.materialDetailText}</span>
            <HBIcon className={styles.icon} type="ic_Next21" />
          </div>
        </a>
        <div className={styles.right}>
          <div className={styles.id}>ID:{info.id}</div>
        </div>
      </div>
      {infoAbtest.old ? (
        <>
          <div className={styles.param}>
            <div className={styles.item}>
              <div className={styles.label}>素材分类</div>
              <div className={styles.content}>
                {material.material_category === "高端免抠"
                  ? "免抠元素"
                  : material.material_category}
              </div>
            </div>
            <When condition={size}>
              <div className={styles.item}>
                <div className={styles.label}>素材规格</div>
                <div className={styles.content}>
                  {size?.width}x{size?.height}px
                  <When
                    condition={
                      pin?.material_info?.dpi >= 45 &&
                      !isThirdPartyMaterial(pin?.material_info)
                    }
                  >
                    {`（${pin?.material_info?.dpi} dpi）`}
                  </When>
                </div>
              </div>
            </When>
            <When condition={material.material_category === "视频"}>
              <div className={styles.item}>
                <div className={styles.label}>
                  分<span className={styles.space3}></span>辨
                  <span className={styles.space3}></span>率
                </div>
                <div className={styles.content}>1080p</div>
              </div>
            </When>
            {/* <When condition={pin?.extra?.aigc === 1}>
          <div className={styles.item}>
            <div className={styles.label}>创作来源</div>
            <div className={styles.content}>
              设计师基于自有创意使用 AI 绘图工具创造
            </div>
          </div>
        </When> */}
          </div>
          {copyright && (
            <div className={styles.copyright}>
              <div className={styles.icon}></div>

              <div className={styles.text}>
                {copyright}
                {material.material_category === "平面模板" && (
                  <>
                    ；<br />
                    未获得字体授权，需替换字体使用
                  </>
                )}
                ；
              </div>
            </div>
          )}
          <div className={styles.auth}>
            <div className={styles.item}>
              <div className={styles.label}>授权范围</div>
              <div className={styles.content} data-content-source="素材信息">
                {materialAuth}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          className={classNames([
            styles.materialInfo,
            infoExpand ? styles.expand : null,
          ])}
        >
          <div className={styles.param}>
            <div className={styles.item}>
              <div className={styles.label}>分类</div>
              <div className={styles.content}>
                {material.material_category === "高端免抠"
                  ? "免抠元素"
                  : material.material_category}
              </div>
            </div>
            <When condition={size}>
              <div className={styles.item}>
                <div className={styles.label}>规格</div>
                <div className={styles.content}>
                  {size?.width}x{size?.height}px
                  <When
                    condition={
                      pin?.material_info?.dpi >= 45 &&
                      !isThirdPartyMaterial(pin?.material_info)
                    }
                  >
                    {`（${pin?.material_info?.dpi} dpi）`}
                  </When>
                </div>
              </div>
            </When>
            <When condition={material.material_category === "视频"}>
              <div className={styles.item}>
                <div className={styles.label}>
                  分<span className={styles.space3}></span>辨
                  <span className={styles.space3}></span>率
                </div>
                <div className={styles.content}>1080p</div>
              </div>
            </When>
            {/* <When condition={pin?.extra?.aigc === 1}>
          <div className={styles.item}>
            <div className={styles.label}>创作来源</div>
            <div className={styles.content}>
              设计师基于自有创意使用 AI 绘图工具创造
            </div>
          </div>
        </When> */}
          </div>
          <div className={styles.auth}>
            <div className={styles.item}>
              <div className={styles.label}>授权范围</div>
              <div
                className={styles.content}
                data-content-source="素材信息"
                style={{ color: "#000" }}
              >
                {materialAuth}
              </div>
            </div>
          </div>
          {copyright && (
            <div className={styles.copyright}>
              <div className={styles.icon}></div>

              <div className={styles.text}>
                {copyright}
                {material.material_category === "平面模板" && (
                  <>
                    ；<br />
                    未获得字体授权，需替换字体使用
                  </>
                )}
                ；
              </div>
            </div>
          )}

          <HBIcon
            className={styles.expandIcon}
            type="ic_arrow_down"
            onClick={toogleExpand}
          />
        </div>
      )}
      <div
        className={classNames(styles.btns, infoAbtest.old ? styles.old : null)}
      >
        <UserGuideTooltip
          visible={downloadBubble.visible}
          localStorageKey={(id) => `hb_material_download_bubble_${id}`}
          {...downloadBubble.props}
        >
          <div className={styles.download}>{buttons}</div>
        </UserGuideTooltip>
        {/* 第三方素材并是模板不展示编辑按钮 */}
        <When condition={showEditOnline}>
          <div
            className={styles.editOnline}
            style={
              buttons?.length > 1 ? { marginLeft: "0", width: "100%" } : null
            }
          >
            <Button
              className={classNames([styles.button, styles.outline])}
              icon={infoAbtest.old && <HBIcon type="ic_edit" />}
              type="text"
              data-tooltip="支持抠图、裁切、拓展等功能"
              data-tooltip-position="bottom"
              data-gdmerge-abcontrol
              data-gd-click="button_click"
              data-gd-expose="button_expose"
              data-button-name="在线编辑"
              data-content-source="素材采集"
              data-content-category={
                pin?.material_info.categories?.includes("模板")
                  ? "模板"
                  : "元素材"
              }
              onClick={() => {
                const downloadProcess = new DownloadProcess({
                  pin,
                  searchWord,
                });
                downloadProcess.processPinDetailEditOnline();
              }}
            >
              在线编辑
            </Button>
          </div>
        </When>
      </div>
    </div>
  );
}

export function PinMaterialRedirect({ pin, title }) {
  const { config } = useAppStore(({ config }) => ({
    config,
  }));

  return (
    <div className={styles.material}>
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.file_material?.material_id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.name}>花瓣素材</div>
          <div className={styles.link}>
            <span className={styles.text}>{config?.materialDetailText}</span>
            <HBIcon className={styles.icon} type="ic_Next21" />
          </div>
        </a>
      </div>
      <a
        className={styles.redirect}
        href={`/pins/${pin?.file_material?.pin_id}`}
        target="_blank"
      >
        <div className={styles.content}>
          <HBIcon className={styles.icon} type="error_outline" />
          <div className={styles.text}>当前素材已更新，前往最新版查看下载</div>
        </div>
        <HBIcon className={styles.arrow} type="ic_chevron_right" />
      </a>
    </div>
  );
}
export default memo(PinMaterial);
